import React, { useState } from 'react';
import styled from '@emotion/styled';
import { mqMin } from 'utils/responsive';
import { useAnnouncements } from './useAnnouncements';
import dayjs from 'dayjs';
import { useModal } from 'ui/components/screens/modals/useModal';
import { AnnouncementDetail } from 'ui/components/screens/modals/AnnouncementDetail/AnnouncementDetail';
import { IAnnouncement, ANNOUNCEMENT_CATEGORY_LABELS } from 'domain/entities/Announcement/Announcement';
import { Only } from 'ui/components/elements/Only/Only';

const Container = styled.section<{ disabed: boolean }>`
    display: ${(props) => (props.disabed ? 'none' : 'block')};
    position: relative;
    padding: 20px 0;
    background-color: var(--lightblue);
`;

const H2 = styled.h2`
    text-align: center;
    font-size: 1.25rem;
    line-height: 1.7em;
    letter-spacing: 0.05em;
    margin-bottom: 32px;

    strong {
        color: var(--orange);
    }

    ${mqMin('NARROW')} {
        font-size: 1.5rem;
        line-height: 1.8em;
        letter-spacing: 0.1em;
        margin-bottom: 43px;
    }
`;

const AnnouncementsWrapper = styled.div<{ itemCount: number }>`
    width: 88.37209302325581%;
    margin: 0 auto;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    ${mqMin('NARROW')} {
        width: 100%;
        max-width: 900px;
    }
`;

const AnnouncementItem = styled.div`
    background-color: #fff;
    border-radius: 11px;
    padding: 20px;
    margin-bottom: 15px;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }

    &:last-child {
        margin-bottom: 0;
    }

    ${mqMin('NARROW')} {
        padding: 15px 25px;
    }
`;

const AnnouncementHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const AnnouncementTitle = styled.h3`
    font-size: 1rem;
    line-height: 1.6em;
    letter-spacing: 0.1em;
    color: var(--dark-blue);

    ${mqMin('NARROW')} {
        font-size: 1.125rem;
    }
`;

const AnnouncementDate = styled.div`
    font-size: 0.75rem;
    color: #666;
    white-space: nowrap;
    margin-left: 10px;
`;

const AnnouncementCategory = styled.span<{ category?: string }>`
    display: inline-block;
    font-size: 0.71rem;
    padding: 4px 8px;
    border-radius: 8px;
    margin-right: 8px;
    background-color: ${(props) => (props.category === 'agent_news' ? '#4CAF50' : 'var(--orange)')};
    color: #fff;
    min-width: 68px;
    text-align: center;
    align-items: center;

    ${mqMin('NARROW')} {
        font-size: 0.78rem;
        padding: 0 10px;
        border-radius: 15px;
        margin-right: 10px;
        margin-left: -8px;
    }
`;

const LoadMoreButton = styled.button`
    display: block;
    width: 100%;
    padding: 10px;
    background: none;
    border: none;
    font-size: 0.875rem;
    font-weight: bold;
    cursor: pointer;
    color: var(--dark-blue);
    transition: opacity 0.2s;
    margin-top: 10px;

    &:hover {
        opacity: 0.7;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

export const Announcements: React.FC = () => {
    const { announcements, hasMore, isLoading, loadMore } = useAnnouncements();
    const { openModal, closeModal, createModal } = useModal();
    const [selectedAnnouncement, setSelectedAnnouncement] = useState<IAnnouncement | null>(null);

    const handleAnnouncementClick = (announcement: IAnnouncement) => {
        setSelectedAnnouncement(announcement);
        openModal();
    };

    const handleCloseModal = () => {
        closeModal();
        setSelectedAnnouncement(null);
    };

    return (
        <Container id="news" disabed={announcements.length === 0}>
            <H2 className="fadein fadein_y">
                最新の<strong>お知らせ</strong>
            </H2>
            <AnnouncementsWrapper className="fadein fadein_y" itemCount={announcements.length}>
                <Only pc>
                    {announcements.map((announcement) => (
                        <AnnouncementItem key={announcement.id} onClick={() => handleAnnouncementClick(announcement)}>
                            <AnnouncementHeader>
                                <AnnouncementTitle>
                                    {announcement.category && (
                                        <AnnouncementCategory category={announcement.category}>
                                            {ANNOUNCEMENT_CATEGORY_LABELS[announcement.category]}
                                        </AnnouncementCategory>
                                    )}
                                    {announcement.title}
                                </AnnouncementTitle>
                                <AnnouncementDate>
                                    {dayjs(announcement.created_at).format('YYYY.MM.DD')}
                                </AnnouncementDate>
                            </AnnouncementHeader>
                        </AnnouncementItem>
                    ))}
                </Only>
                <Only sp>
                    {announcements.map((announcement) => (
                        <AnnouncementItem key={announcement.id} onClick={() => handleAnnouncementClick(announcement)}>
                            <AnnouncementHeader>
                                {announcement.category && (
                                    <AnnouncementCategory category={announcement.category}>
                                        {ANNOUNCEMENT_CATEGORY_LABELS[announcement.category]}
                                    </AnnouncementCategory>
                                )}
                                <AnnouncementDate>
                                    {dayjs(announcement.created_at).format('YYYY.MM.DD')}
                                </AnnouncementDate>
                            </AnnouncementHeader>
                            <AnnouncementTitle>{announcement.title}</AnnouncementTitle>
                        </AnnouncementItem>
                    ))}
                </Only>

                {hasMore && (
                    <LoadMoreButton onClick={loadMore} disabled={isLoading}>
                        {isLoading ? '読み込み中...' : 'もっと見る'}
                    </LoadMoreButton>
                )}
            </AnnouncementsWrapper>
            {selectedAnnouncement &&
                createModal(<AnnouncementDetail announcement={selectedAnnouncement} onClose={handleCloseModal} />, {
                    keyboard: true,
                    maskClosable: true,
                    onClose: handleCloseModal,
                    scrollable: true,
                })}
        </Container>
    );
};
